/**
 * Avatar d'interlocuteur — vraies photos libres de droits, mapping déterministe.
 *
 * Source : randomuser.me (set de ~100 portraits homme + ~100 femme, libres pour
 * usage commercial et non commercial). Aucun vrai client n'est concerné (visages
 * de banque) → RGPD-safe. Le genre est inféré du prénom et l'index est dérivé
 * d'un hash du nom → chaque prospect a un visage stable et cohérent.
 *
 * Chargé en https (la CSP img-src autorise déjà https:). Prévoir un fallback UI
 * (initiales) en cas d'indisponibilité du CDN — cf. CallStage onError.
 *
 * NB prod : envisager de miroiter ces portraits sur R2 pour ne pas dépendre d'un
 * CDN tiers en production.
 */

export type Gender = "female" | "male" | "unknown";

// Prénoms français courants — couvrent l'essentiel des prospects générés par N8N.
const FEMALE_NAMES = new Set([
  "marie", "sophie", "julie", "camille", "laura", "emma", "léa", "lea", "chloé", "chloe",
  "manon", "sarah", "laure", "claire", "anne", "céline", "celine", "nathalie", "isabelle",
  "sandrine", "stéphanie", "stephanie", "valérie", "valerie", "caroline", "aurélie", "aurelie",
  "delphine", "audrey", "elodie", "élodie", "pauline", "charlotte", "alice", "lucie", "margaux",
  "mathilde", "amandine", "virginie", "christine", "catherine", "sylvie", "patricia", "florence",
  "hélène", "helene", "véronique", "veronique", "martine", "monique", "françoise", "francoise",
  "agnès", "agnes", "estelle", "fanny", "inès", "ines", "jeanne", "louise", "clara", "eva",
  "juliette", "noémie", "noemie", "océane", "oceane", "anaïs", "anais", "maud", "carole",
]);
const MALE_NAMES = new Set([
  "pierre", "philippe", "jean", "michel", "alain", "nicolas", "thomas", "julien", "david",
  "laurent", "frédéric", "frederic", "sébastien", "sebastien", "stéphane", "stephane", "olivier",
  "christophe", "patrick", "vincent", "bernard", "daniel", "éric", "eric", "pascal", "gérard",
  "gerard", "marc", "antoine", "guillaume", "françois", "francois", "maxime", "alexandre", "benoît",
  "benoit", "romain", "fabien", "ludovic", "cédric", "cedric", "jérôme", "jerome", "didier",
  "gilles", "henri", "louis", "paul", "jacques", "claude", "rené", "rene", "denis", "bruno",
  "hervé", "herve", "thierry", "yves", "raphaël", "raphael", "hugo", "lucas", "mathieu", "matthieu",
  "arnaud", "damien", "florian", "quentin", "kevin", "kévin", "loïc", "loic", "xavier", "régis", "regis",
]);

export function guessGender(fullName: string): Gender {
  const first = (fullName || "").trim().split(/\s+/)[0]?.toLowerCase() || "";
  if (!first) return "unknown";
  if (FEMALE_NAMES.has(first)) return "female";
  if (MALE_NAMES.has(first)) return "male";
  return "unknown";
}

// Hash déterministe simple (FNV-1a 32 bits) → entier positif.
function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

const PORTRAIT_COUNT = 100; // randomuser : indices 0..99 par genre

/**
 * URL d'un portrait humain stable pour un interlocuteur donné.
 * @param seed identifiant stable — typiquement le nom du prospect généré.
 */
export function personaAvatarUrl(seed: string): string {
  const safeSeed = seed && seed.trim().length > 0 ? seed.trim() : "prospect";
  const h = hashSeed(safeSeed);
  const gender = guessGender(safeSeed);
  // Genre inconnu → réparti de façon déterministe (bit de poids fort du hash).
  const folder = gender === "female" ? "women" : gender === "male" ? "men" : (h & 0x80000000 ? "women" : "men");
  const index = h % PORTRAIT_COUNT;
  return `https://randomuser.me/api/portraits/${folder}/${index}.jpg`;
}
